import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, TextInput } from '@bravemove/ui-web';
import { CompassWordmark } from '@bravemove/design-tokens/brand';
import { adminFetch } from '../auth/apiClient';
import { ApiError } from '../auth/types';
import { translateApiError } from '../auth/errors';
import { AuthLayout } from '../layout/AuthLayout';

interface FormValues {
  readonly newPassword: string;
  readonly confirmPassword: string;
}

const strongPassword = z
  .string()
  .min(12)
  .refine((v) => /[a-z]/.test(v), { message: 'lower' })
  .refine((v) => /[A-Z]/.test(v), { message: 'upper' })
  .refine((v) => /\d/.test(v), { message: 'digit' })
  .refine((v) => /[^A-Za-z0-9]/.test(v), { message: 'symbol' });

export function ResetPasswordPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const params = useParams<{ token: string }>();
  const token = params.token ?? '';
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);

  const schema = z
    .object({
      newPassword: strongPassword,
      confirmPassword: z.string(),
    })
    .refine((d) => d.newPassword === d.confirmPassword, {
      message: t('admin:validation.passwordMismatch'),
      path: ['confirmPassword'],
    });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      await adminFetch('/admin/auth/reset-password', {
        method: 'POST',
        body: { token, newPassword: values.newPassword },
      });
      setSuccess(true);
    } catch (err) {
      if (
        err instanceof ApiError &&
        // FIX 5.23 — the reset route answers RESET_TOKEN_EXPIRED; the invite
        // codes are kept for an older API.
        (err.code === 'RESET_TOKEN_EXPIRED' ||
          err.code === 'INVITE_EXPIRED' ||
          err.code === 'INVITE_CONSUMED')
      ) {
        setExpired(true);
        return;
      }
      setServerError(translateApiError(err, t));
    }
  });

  if (expired) {
    return (
      <AuthLayout>
        <header className="text-center mb-6">
          <CompassWordmark size={28} />
          <h1 className="text-h2 font-bold text-ink mt-4">
            {t('admin:resetPassword.expiredTitle')}
          </h1>
          <p className="text-body text-ink-2 mt-1">{t('admin:resetPassword.expiredBody')}</p>
        </header>
        <Button
          type="button"
          variant="primary"
          fullWidth
          onClick={() => navigate('/forgot-password')}
        >
          {t('admin:resetPassword.requestNew')}
        </Button>
      </AuthLayout>
    );
  }

  if (success) {
    return (
      <AuthLayout>
        <header className="text-center mb-6">
          <CompassWordmark size={28} />
          <h1 className="text-h2 font-bold text-ink mt-4">
            {t('admin:resetPassword.successTitle')}
          </h1>
        </header>
        <Banner tone="success" className="mb-4">
          {t('admin:resetPassword.successBody')}
        </Banner>
        <Button type="button" variant="primary" fullWidth onClick={() => navigate('/login')}>
          {t('admin:forgotPassword.backToLogin')}
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <header className="text-center mb-6">
        <CompassWordmark size={28} />
        <h1 className="text-h2 font-bold text-ink mt-4">{t('admin:resetPassword.title')}</h1>
        <p className="text-body text-ink-2 mt-1">{t('admin:resetPassword.subtitle')}</p>
      </header>
      {serverError ? (
        <Banner tone="danger" className="mb-4">
          {serverError}
        </Banner>
      ) : null}
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <FormField
          label={t('admin:common.newPassword')}
          error={
            form.formState.errors.newPassword ? t('admin:validation.passwordStrength') : undefined
          }
          required
        >
          <TextInput
            type="password"
            autoComplete="new-password"
            {...form.register('newPassword')}
          />
        </FormField>
        <FormField
          label={t('admin:common.confirmPassword')}
          error={form.formState.errors.confirmPassword?.message}
          required
        >
          <TextInput
            type="password"
            autoComplete="new-password"
            {...form.register('confirmPassword')}
          />
        </FormField>
        <Button type="submit" variant="primary" fullWidth loading={form.formState.isSubmitting}>
          {t('admin:resetPassword.submit')}
        </Button>
      </form>
    </AuthLayout>
  );
}
